import { z } from 'zod';
import { ChartKind } from '../../types/savedCharts';
import assertUnreachable from '../../utils/assertUnreachable';
import { getColumnAxisType, SortByDirection, VizAggregationOptions, VizIndexType, } from '../../visualizations/types';
import { buildComposerVizConfig, getComposerChartKind, getComposerFieldConfig, getComposerVizPlan, isComposerNumericColumn, } from './composerViz';
/** Switcher order. */
export const COMPOSER_VIZ_KINDS = [
    'table',
    'bar',
    'line',
    'pie',
    'big_number',
];
/** The SQL runner's set; ANY first because node results are already aggregated. */
export const COMPOSER_VIZ_AGGREGATIONS = [
    VizAggregationOptions.ANY,
    VizAggregationOptions.SUM,
    VizAggregationOptions.AVERAGE,
    VizAggregationOptions.MIN,
    VizAggregationOptions.MAX,
    VizAggregationOptions.COUNT,
];
const tableConfig = () => ({
    type: ChartKind.TABLE,
    metadata: { version: 1 },
    columns: {},
    display: undefined,
});
const columnsByReference = (columns) => new Map(columns.map((column) => [column.reference, column]));
const layoutOf = (value) => value.type === ChartKind.TABLE ? null : (value.fieldConfig ?? null);
/** Every referenced column exists in the result and every y is numeric. */
export const composerVizConfigFitsColumns = (vizConfig, columns) => {
    const known = columnsByReference(columns);
    if (vizConfig.type === ChartKind.TABLE) {
        return Object.keys(vizConfig.columns).every((reference) => known.has(reference));
    }
    const layout = vizConfig.fieldConfig;
    if (!layout || layout.y.length === 0)
        return false;
    if (layout.y.some((y) => {
        const column = known.get(y.reference);
        return !column || !isComposerNumericColumn(column);
    }))
        return false;
    if (vizConfig.type !== ChartKind.BIG_NUMBER && !layout.x)
        return false;
    if (layout.x && !known.has(layout.x.reference))
        return false;
    if ((layout.groupBy ?? []).some((g) => !known.has(g.reference)))
        return false;
    return (layout.sortBy ?? []).every((s) => known.has(s.reference));
};
/** Table always; chart kinds need a numeric column; bar/line/pie also need a second column for x; a big number needs a single row. */
export const getAvailableComposerVizKinds = (columns, rows) => {
    const hasNumeric = columns.some(isComposerNumericColumn);
    return COMPOSER_VIZ_KINDS.filter((kind) => {
        switch (kind) {
            case 'table':
                return true;
            case 'big_number':
                return hasNumeric && rows.length === 1;
            case 'bar':
            case 'line':
            case 'pie':
                return hasNumeric && columns.length >= 2;
            default:
                return assertUnreachable(kind, 'Unknown composer viz kind');
        }
    });
};
const xOf = (column) => ({
    reference: column.reference,
    type: getColumnAxisType(column.type),
});
/** First non-numeric column, else the first column that is not the value. */
const pickDefaultX = (columns, layout) => {
    const used = new Set(layout.y.map((y) => y.reference));
    return (columns.find((column) => !isComposerNumericColumn(column)) ??
        columns.find((column) => !used.has(column.reference)));
};
/** Column-type default for one kind; null when the columns cannot chart it. */
const defaultLayoutFor = (kind, { columns, rows, node }) => {
    const plan = getComposerVizPlan({ columns, rows, node, vizConfig: null });
    const axes = plan.axes[kind];
    if (axes)
        return getComposerFieldConfig(axes, kind);
    const y = columns.find(isComposerNumericColumn);
    if (!y)
        return null;
    const layout = {
        x: undefined,
        y: [{ reference: y.reference, aggregation: VizAggregationOptions.ANY }],
        groupBy: [],
    };
    if (kind === 'big_number')
        return layout;
    const x = pickDefaultX(columns, layout);
    return x ? { ...layout, x: xOf(x) } : null;
};
/** The panel's value: the stored viz config when every referenced column is still in the result, else the table. */
export const getComposerVizPanelValue = ({ vizConfig, columns, }) => vizConfig && composerVizConfigFitsColumns(vizConfig, columns)
    ? vizConfig
    : tableConfig();
export const getComposerVizPanelOptions = (value, columns, rows) => {
    const xReference = layoutOf(value)?.x?.reference;
    const numeric = columns.filter(isComposerNumericColumn);
    const other = columns.filter((column) => !isComposerNumericColumn(column));
    return {
        kinds: getAvailableComposerVizKinds(columns, rows),
        x: [...other, ...numeric],
        y: numeric,
        groupBy: other.filter((column) => column.reference !== xReference),
        aggregations: COMPOSER_VIZ_AGGREGATIONS,
    };
};
/** Like the SQL runner, a sort only orders the x axis; it goes when x changes or is gone. */
const withSortOnX = (layout) => {
    const sort = layout.sortBy?.[0];
    if (sort && layout.x && sort.reference === layout.x.reference) {
        return { ...layout, sortBy: [sort] };
    }
    const { sortBy: _sortBy, ...rest } = layout;
    return rest;
};
const withLayout = (kind, layout) => buildComposerVizConfig({ kind, fieldConfig: withSortOnX(layout) });
const editLayout = (value, edit) => {
    if (value.type === ChartKind.TABLE || !value.fieldConfig)
        return value;
    return withLayout(getComposerChartKind(value), edit(value.fieldConfig));
};
/** bar/line keep everything; pie drops the split and extra y; big number keeps only the first y. */
const applyKindRules = (kind, layout, columns) => {
    const ensureX = (base) => {
        if (base.x)
            return base;
        const x = pickDefaultX(columns, base);
        return x ? { ...base, x: xOf(x) } : null;
    };
    switch (kind) {
        case 'bar':
        case 'line':
            return ensureX({ ...layout, groupBy: layout.groupBy ?? [] });
        case 'pie':
            return ensureX({ ...layout, y: layout.y.slice(0, 1), groupBy: [] });
        case 'big_number':
            return {
                x: undefined,
                y: layout.y.slice(0, 1),
                groupBy: [],
            };
        default:
            return assertUnreachable(kind, 'Unknown composer chart kind');
    }
};
/**
 * Switches the kind. Field config carries over by the kind rules; table drops
 * it, and leaving table restores `remembered` when it still fits, else the
 * column-type default for that kind.
 */
export const switchComposerVizKind = (value, kind, { remembered, ...result }) => {
    if (kind === 'table')
        return tableConfig();
    const source = (() => {
        if (value.type !== ChartKind.TABLE)
            return value;
        if (remembered &&
            remembered.type !== ChartKind.TABLE &&
            composerVizConfigFitsColumns(remembered, result.columns))
            return remembered;
        return null;
    })();
    const layout = source?.fieldConfig ?? defaultLayoutFor(kind, result);
    if (!layout)
        return tableConfig();
    const next = applyKindRules(kind, layout, result.columns);
    return next ? withLayout(kind, next) : tableConfig();
};
export const setComposerVizX = (value, reference, columns) => {
    const column = columnsByReference(columns).get(reference);
    if (!column)
        return value;
    return editLayout(value, (layout) => ({
        ...layout,
        x: xOf(column),
        groupBy: (layout.groupBy ?? []).filter((g) => g.reference !== reference),
    }));
};
export const setComposerVizY = (value, index, patch) => editLayout(value, (layout) => ({
    ...layout,
    y: layout.y.map((y, i) => (i === index ? { ...y, ...patch } : y)),
}));
/** Adds the next numeric column not already charted; no-op when none is left. */
export const addComposerVizY = (value, columns) => editLayout(value, (layout) => {
    const used = new Set(layout.y.map((y) => y.reference));
    const next = columns.find((column) => isComposerNumericColumn(column) && !used.has(column.reference));
    if (!next)
        return layout;
    return {
        ...layout,
        y: [
            ...layout.y,
            {
                reference: next.reference,
                aggregation: VizAggregationOptions.ANY,
            },
        ],
    };
});
/** Never drops the last y. */
export const removeComposerVizY = (value, index) => editLayout(value, (layout) => layout.y.length <= 1
    ? layout
    : { ...layout, y: layout.y.filter((_, i) => i !== index) });
export const setComposerVizGroupBy = (value, reference) => editLayout(value, (layout) => ({
    ...layout,
    groupBy: reference ? [{ reference }] : [],
}));
/** The x axis sort direction; null when the x axis keeps its own order. */
export const getComposerVizSort = (value) => {
    const layout = layoutOf(value);
    const sort = layout?.sortBy?.[0];
    return sort && sort.reference === layout?.x?.reference
        ? sort.direction
        : null;
};
export const setComposerVizSort = (value, direction) => editLayout(value, (layout) => {
    const { sortBy: _sortBy, ...rest } = layout;
    if (!direction || !layout.x)
        return rest;
    return {
        ...rest,
        sortBy: [{ reference: layout.x.reference, direction }],
    };
});
export const getComposerVizKindLabel = (kind) => {
    switch (kind) {
        case 'table':
            return 'Table';
        case 'bar':
            return 'Bar';
        case 'line':
            return 'Line';
        case 'pie':
            return 'Pie';
        case 'big_number':
            return 'Big value';
        default:
            return assertUnreachable(kind, 'Unknown composer viz kind');
    }
};
const AGGREGATION_PREFIX = {
    [VizAggregationOptions.ANY]: '',
    [VizAggregationOptions.SUM]: 'sum',
    [VizAggregationOptions.AVERAGE]: 'avg',
    [VizAggregationOptions.MIN]: 'min',
    [VizAggregationOptions.MAX]: 'max',
    [VizAggregationOptions.COUNT]: 'count',
};
/** `x × y by groupBy`, labels over references, aggregation prefixed on y when not ANY, at most two y names. */
export const summarizeComposerVizConfig = (value, columns) => {
    if (value.type === ChartKind.TABLE)
        return 'Table';
    const layout = value.fieldConfig;
    if (!layout)
        return getComposerVizKindLabel(getComposerChartKind(value));
    const known = columnsByReference(columns);
    const name = (reference) => known.get(reference)?.label ?? reference;
    const ys = layout.y.map((y) => [AGGREGATION_PREFIX[y.aggregation], name(y.reference)]
        .filter(Boolean)
        .join(' '));
    const shownYs = ys.slice(0, 2).join(', ');
    const yText = ys.length > 2 ? `${shownYs} +${ys.length - 2}` : shownYs;
    if (value.type === ChartKind.BIG_NUMBER || !layout.x)
        return yText;
    const groupBy = layout.groupBy?.[0];
    const split = value.type === ChartKind.PIE || !groupBy
        ? ''
        : ` by ${name(groupBy.reference)}`;
    return `${name(layout.x.reference)} × ${yText}${split}`;
};
/** Columns a chart draws from; null when one is missing from the result. */
export const resolveComposerVizColumns = (layout, columns) => {
    const known = columnsByReference(columns);
    const x = layout.x ? (known.get(layout.x.reference) ?? null) : null;
    if (layout.x && !x)
        return null;
    const y = layout.y.map((item) => known.get(item.reference));
    if (y.some((column) => column === undefined))
        return null;
    const groupByReference = layout.groupBy?.[0]?.reference;
    const groupBy = groupByReference
        ? (known.get(groupByReference) ?? null)
        : null;
    if (groupByReference && !groupBy)
        return null;
    return {
        x,
        y: y.filter((c) => c !== undefined),
        groupBy,
    };
};
/** True when the chart needs the pivoted re-run: a series split or a real aggregation. An x sort alone orders the fetched rows. */
export const composerVizNeedsPivot = (layout) => (layout.groupBy?.length ?? 0) > 0 ||
    layout.y.some((y) => y.aggregation !== VizAggregationOptions.ANY);
const referenceSchema = z.string().min(1);
const metadataSchema = z.object({ version: z.number().int() }).strict();
const layoutSchema = z
    .object({
    x: z
        .object({ reference: referenceSchema, type: z.enum(VizIndexType) })
        .strict()
        .optional(),
    y: z
        .array(z
        .object({
        reference: referenceSchema,
        aggregation: z.enum(VizAggregationOptions),
    })
        .strict())
        .min(1),
    groupBy: z
        .array(z.object({ reference: referenceSchema }).strict())
        .optional(),
    sortBy: z
        .array(z
        .object({
        reference: referenceSchema,
        direction: z.enum(SortByDirection),
        nullsFirst: z.boolean().optional(),
    })
        .strict())
        .optional(),
})
    .strict();
const chartSchema = (type) => z
    .object({
    type: z.literal(type),
    metadata: metadataSchema,
    fieldConfig: layoutSchema,
    display: z.undefined().optional(),
})
    .strict();
const tableColumnSchema = z
    .object({
    visible: z.boolean(),
    reference: referenceSchema,
    label: z.string(),
    frozen: z.boolean(),
    order: z.number().optional(),
})
    .strict();
/** The stored composer viz config: the five kinds, field config only, no display options. */
export const composerVizConfigSchema = z.discriminatedUnion('type', [
    chartSchema(ChartKind.VERTICAL_BAR),
    chartSchema(ChartKind.LINE),
    chartSchema(ChartKind.PIE),
    chartSchema(ChartKind.BIG_NUMBER),
    z
        .object({
        type: z.literal(ChartKind.TABLE),
        metadata: metadataSchema,
        columns: z.record(referenceSchema, tableColumnSchema),
        display: z.undefined().optional(),
    })
        .strict(),
]);
/** Validates an untrusted viz config and normalises it to the stored shape. */
export const parseComposerVizConfig = (input) => {
    const parsed = composerVizConfigSchema.safeParse(input);
    if (!parsed.success) {
        return { ok: false, error: z.prettifyError(parsed.error) };
    }
    const { data } = parsed;
    if (data.type === ChartKind.TABLE) {
        return {
            ok: true,
            vizConfig: {
                type: ChartKind.TABLE,
                metadata: data.metadata,
                columns: data.columns,
                display: undefined,
            },
        };
    }
    const { fieldConfig } = data;
    const layout = {
        x: fieldConfig.x,
        y: fieldConfig.y,
        groupBy: fieldConfig.groupBy ?? [],
        ...(fieldConfig.sortBy ? { sortBy: fieldConfig.sortBy } : {}),
    };
    return {
        ok: true,
        vizConfig: withLayout(getComposerChartKind(data), layout),
    };
};
//# sourceMappingURL=composerVizPanel.js.map